/**
 * Reads a value off a row by id, walking into nested objects when the id is dotted.
 *
 * Lets a column address a key inside the row's dynamic attributes —
 * `attributes.DEALER`. Attributes arrive from the API as a JSON string, parsed
 * here on first reach, so callers declare a path and nothing else.
 *
 * A step ending in brackets reaches into a collection, and says which one of it
 * it means in one of two ways. `contacts[$contactIndex]` takes the one the page
 * says is in view, following it as it moves. `contactAddresses[addressTypeId=1]`
 * takes the one that answers to something, which is what is wanted when the
 * choice is a rule rather than a position - the home address is the home
 * address wherever it sits in the list.
 *
 * The catalogue offers such a key with empty brackets, which the layout fills
 * in; brackets left empty, or naming a parameter the page does not publish,
 * read the first of them.
 *
 * @param {Object} data - The row.
 * @param {String} id - A property name, or a dotted path into it.
 * @param {Object} parameters - Values the page publishes, for the indexes named in the path.
 *
 * @returns {*} The value, or undefined if any step is missing.
 */

const safeParse = (value) => {
  try {
    return JSON.parse(value);
  } catch {
    return undefined;
  }
};

/** A step of a path, split into the property it names and what it takes out of it. */
const STEP = /^([^[]*)(?:\[([^\]]*)\])?$/;

/** Which one of a collection a step takes. */
const oneOf = (held, chooses, parameters) => {
  if (!chooses) return held?.[0];

  // A rule about the one that is wanted, rather than where it happens to sit.
  if (chooses.includes('=')) {
    const [name, value] = chooses.split('=');

    return held?.find?.((item) => String(item?.[name]) === value);
  }

  const at = chooses.startsWith('$')
    ? parameters?.[chooses.slice(1)]
    : Number(chooses);

  return held?.[Number.isInteger(at) ? at : 0];
};

const resolveValuePath = (data, id, parameters = {}) => {
  if (!id || !data) return undefined;
  if (!id.includes('.') && !id.includes('[')) return data[id];

  return id.split('.').reduce((node, step) => {
    if (node === null || node === undefined) return undefined;

    const value = typeof node === 'string' ? safeParse(node) : node;
    const [, key, chooses] = step.match(STEP) ?? [];
    const held = key ? value?.[key] : value;

    return chooses === undefined ? held : oneOf(held, chooses, parameters);
  }, data);
};

/**
 * A path with the indexes taken out of it, which is what makes two of them the same key.
 *
 * The same property read at two different indexes is the same property: a picker offering what a
 * layout does not already show, or a lookup of a key in the catalogue, is asking about the property
 * rather than about which one of them.
 *
 * @param {String} id - A property name, or a dotted path into it.
 *
 * @returns {String}
 */
export const valuePathShape = (id) => (id ?? '').replace(/\[[^\]]*\]/g, '[]');

export default resolveValuePath;
